import React, { Component } from 'react';
import { connect } from 'react-redux';
import {
  Route,
  Redirect,
  Switch,
  withRouter,
} from 'react-router-dom';
import PropTypes from 'prop-types';
import 'core-js/features/promise';
import 'core-js/features/object/assign';
import 'core-js/features/array/flat';
import 'core-js/features/array/find';
import ModalRoot from './components/common/Modals/ModalRoot';
import NavBar from './components/common/layout/NavBar/NavBar';
import Profile from './components/profile/Profile';
import Videos from './components/videos/Videos';
import Newsletters from './components/newsletters/Newsletters';
import Reviews from './components/reviews/Reviews';
import ConnectedFooter from './components/common/layout/Footer/Footer';
import ConnectedContact from './components/contact/Contact';
import ContactRefer from './components/contact/ContactRefer';
import Subscribe from './components/subscribe/Subscribe';
import Unsubscribe from './components/unsubscribe/Unsubscribe';
import SingleNewsletter from './components/singleNewsletter/SingleNewsletter';
import KWR from './components/kellerWilliamsRecruting/KWR';
import Chat from './components/common/chatWindow/Chat';
import { getProfile } from './actions/profileActions';
import { returnCtaFormParams, includes, isBrowserSupported } from './util/helperFuncs';
import { campaignPaths } from './util/formTypeConfig';
import Spinner from './components/common/Spinner';
import 'bootstrap/dist/css/bootstrap.css';
import './App.scss';
import CustomPage from './components/customPage/CustomPage';
import MLSListings from './components/mlsListings/MLSListings';
import { initGA, logPageView, refreshGa4FromConsent } from './components/common/Google/ReactGA';
import ProfileDidomiSdk from './components/common/Didomi/ProfileDidomiSdk';
import {
  getAnalyticsConsent,
  hasAcceptedAnalytics,
  hasAcceptedPostHog,
  onConsentChange,
} from './util/analyticsConsent';
import {
  initPostHog,
  capturePageView,
  capturePageLeave,
  shutdownPostHog,
  isPostHogInitialized,
} from './util/postHogProfile';
import FeedbackSuccess from './components/feedbackSuccess/FeedbackSuccess';
import NotSupported from './components/browserNotSupported/NotSupported';
import SecretsActions from './actions/SecretsActions';
import SecretsService from './services/SecretsService';

export class App extends Component {
  constructor(props) {
    super(props);
    this.state = {
      analyticsTagsAdded: false,
      faviconUpdated: false,
    };
    this.previousUrl = typeof window !== 'undefined' ? window.location.href : '';
  }

  componentDidMount() {
    this.loadProfileAndReCaptchaSiteKey();
    this.initAnalyticsIfConsented();
    this.unsubscribeConsent = onConsentChange(this.handleAnalyticsConsentChange);
    const { history } = this.props;
    this.unlistenHistory = history.listen((location) => {
      if (hasAcceptedAnalytics()) {
        initGA();
        logPageView(location.pathname + (location.search || ''));
      }
      if (hasAcceptedPostHog() && isPostHogInitialized()) {
        capturePageLeave(this.previousUrl);
        capturePageView();
      }
      this.previousUrl = `${window.location.origin}${location.pathname}${location.search || ''}`;
    });
  }

  componentDidUpdate() {
    this.updateIndexHeadTag();
    const { profile: profileState } = this.props;
    const platformAnalytics = profileState.profile && profileState.profile.platform_analytics;
    if (hasAcceptedAnalytics() && hasAcceptedPostHog()) {
      this.tryStartPostHog(platformAnalytics);
    }
  }

  componentWillUnmount() {
    if (this.unlistenHistory) {
      this.unlistenHistory();
    }
    if (this.unsubscribeConsent) {
      this.unsubscribeConsent();
    }
  }

  tryStartPostHog = (platformAnalytics) => {
    if (!hasAcceptedAnalytics() || !hasAcceptedPostHog() || isPostHogInitialized()) {
      return;
    }
    if (!platformAnalytics) {
      return;
    }
    if (initPostHog(platformAnalytics, { queueInitialPageView: true })) {
      this.previousUrl = window.location.href;
    }
  };

  handleAnalyticsConsentChange = () => {
    const { profile: profileState } = this.props;
    const platformAnalytics = profileState.profile && profileState.profile.platform_analytics;

    if (!hasAcceptedAnalytics() || !hasAcceptedPostHog()) {
      shutdownPostHog();
      refreshGa4FromConsent();
      if (!hasAcceptedAnalytics()) {
        this.setState({ analyticsTagsAdded: true });
      }
      this.forceUpdate();
      return;
    }

    if (getAnalyticsConsent() === 'accepted') {
      this.setState({ analyticsTagsAdded: false }, () => {
        this.initAnalyticsIfConsented();
        this.updateIndexHeadTag();
        this.tryStartPostHog(platformAnalytics);
      });
    }
    this.forceUpdate();
  };

  initAnalyticsIfConsented = () => {
    if (!hasAcceptedAnalytics()) return;
    const { location } = this.props;
    initGA();
    logPageView(location.pathname + (location.search || ''));
  };

  loadProfileAndReCaptchaSiteKey = () => {
    const { getProfile, secretsActions } = this.props;
    getProfile();
    secretsActions.getReCaptchaV2SiteKey();
  };

  updateIndexHeadTag() {
    const { analyticsTagsAdded, faviconUpdated } = this.state;
    const { profile } = this.props;

    if (!profile.profile || !profile.profile.info) return;

    const { favicon_ico_image, info } = profile.profile;
    let nextFaviconUpdated = faviconUpdated;

    if (!faviconUpdated && favicon_ico_image) {
      const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
      link.type = 'image/x-icon';
      link.rel = 'shortcut icon';
      link.href = favicon_ico_image;
      document.getElementsByTagName('head')[0].appendChild(link);
      nextFaviconUpdated = true;
    }

    if (analyticsTagsAdded) {
      if (nextFaviconUpdated !== faviconUpdated) {
        this.setState({ faviconUpdated: true });
      }
      return;
    }

    const consent = getAnalyticsConsent();
    if (consent === 'declined') {
      shutdownPostHog();
      this.setState({
        analyticsTagsAdded: true,
        faviconUpdated: nextFaviconUpdated,
      });
      return;
    }
    if (!hasAcceptedAnalytics()) {
      if (nextFaviconUpdated !== faviconUpdated) {
        this.setState({ faviconUpdated: true });
      }
      return;
    }

    const thirdPartyHtml = (info.third_party_analytics || '').trim();
    if (!thirdPartyHtml) {
      this.setState({
        analyticsTagsAdded: true,
        faviconUpdated: nextFaviconUpdated,
      });
      return;
    }

    const el = document.createElement('div');
    el.innerHTML = thirdPartyHtml;
    const scripts = el.getElementsByTagName('script');
    for (let i = 0, _i = scripts.length; i < _i; i += 1) {
      const script = document.createElement('script');
      script.async = true;
      if (scripts[i].src) script.src = scripts[i].src;
      if (scripts[i].type) script.type = scripts[i].type;
      if (scripts[i].innerHTML) script.innerHTML = scripts[i].innerHTML;
      document.head.appendChild(script);
    }

    this.setState({
      analyticsTagsAdded: true,
      faviconUpdated: nextFaviconUpdated,
    });
  }

  renderLoadingPage = () => (
    <div className="app" data-toggle="collapse" data-target=".navbar-collapse.show">
      <div className="container">
        <div className="row spinner">
          <div className="col">
            <Spinner />
          </div>
        </div>
      </div>
    </div>
  );

  renderEnabledProfilePageContentAndRoutes = () => {
    const {
      location: { pathname },
      profile: { profile },
    } = this.props;

    const { navigator: { userAgent } } = window;
    const campaignPath = includes(campaignPaths, pathname);
    const feedbackPath = includes(pathname, 'feedback');
    const isSupported = isBrowserSupported(userAgent);

    const { show_reviews, user: { vertical } } = profile;
    const { home_listing_enabled } = profile.info;
    const formShowFlag = profile.info[returnCtaFormParams(pathname, vertical)];

    return (
      <div>
        {!feedbackPath && isSupported && <NavBar />}
        <Switch>
          {!isSupported && <Route component={NotSupported} />}
          <Route exact path="/" component={Profile} />
          <Route exact path="/videos" component={Videos} />
          <Route exact path="/newsletters" component={Newsletters} />
          {show_reviews && <Route exact path="/reviews" component={Reviews} />}
          {home_listing_enabled && (
            <Route exact path="/mls-listings" component={MLSListings} />
          )}
          <Route exact path="/contact" component={ConnectedContact} />
          <Route exact path="/contact/refer" component={ContactRefer} />
          <Route exact path="/single-newsletter/:slug" component={SingleNewsletter} />
          <Route exact path="/single-newsletter/cc/:slug" component={SingleNewsletter} />
          <Route exact path="/unsubscribe/:campaignid/:contactid" component={Unsubscribe} />
          <Route exact path="/unsubscribe/:campaignid" component={Unsubscribe} />
          <Route exact path="/subscribe" component={Subscribe} />
          <Route exact path="/custompage" component={CustomPage} />
          <Route exact path="/p/:slug" component={CustomPage} />
          <Route exact path="/feedback/:campaignid/:contactid" component={FeedbackSuccess} />
          <Route exact path="/feedback/:campaignid" component={FeedbackSuccess} />
          <Route exact path="/subscribe" component={Subscribe} />
          <Route exact path="/joinourteam" component={KWR} />
          {pathname === '/refer' && formShowFlag && (
            <Route
              render={() => <ConnectedContact triggerTypeId="11" />}
            />
          )}
          {campaignPath && formShowFlag && (
            <Route
              path={pathname}
              render={() => <ConnectedContact triggerTypeId={returnCtaFormParams(pathname, vertical, campaignPath)} />}
            />
          )}
          <Route render={() => <Redirect to="/" />} />
        </Switch>
        {!feedbackPath && isSupported && <ConnectedFooter profile={profile} />}
      </div>
    );
  };

  renderDisabledProfilePageRoutes = () => (
    <Switch>
      <Route exact path="/unsubscribe/:campaignid/:contactid" component={Unsubscribe} />
      <Route exact path="/unsubscribe/:campaignid" component={Unsubscribe} />
      <Route component={ConnectedContact} />
    </Switch>
  );

  render() {
    const {
      location: { pathname },
      profile: { profile },
      loading,
      reCaptchaV2SiteKey,
    } = this.props;

    const isLoading = !profile || loading || !reCaptchaV2SiteKey;

    let pageContent;
    if (isLoading) {
      pageContent = this.renderLoadingPage();
    } else {
      const { navigator: { userAgent } } = window;
      const feedbackPath = includes(pathname, 'feedback');
      const isSupported = isBrowserSupported(userAgent);
      const isUnsubscribePage = includes(pathname, 'unsubscribe');

      const isProfilePage = profile.is_enabled;
      const chatEnabled = profile.info.fchat_enabled;

      const showChat = isProfilePage && chatEnabled && !feedbackPath && isSupported && !isUnsubscribePage;

      pageContent = (
        <div className="app" data-toggle="collapse" data-target=".navbar-collapse.show">
          <React.Fragment>
            {
              isProfilePage
                ? this.renderEnabledProfilePageContentAndRoutes()
                : this.renderDisabledProfilePageRoutes()
            }
            <ModalRoot />
          </React.Fragment>
          {showChat && <Chat profile={profile} />}
        </div>
      );
    }

    return (
      <React.Fragment>
        {pageContent}
        <ProfileDidomiSdk />
      </React.Fragment>
    );
  }
}

App.propTypes = {
  getProfile: PropTypes.func.isRequired,
  profile: PropTypes.object.isRequired,
  reCaptchaV2SiteKey: PropTypes.string,
};

App.defaultProps = {
  reCaptchaV2SiteKey: null,
};

const mapStateToProps = state => ({
  profile: state.profile,
  reCaptchaV2SiteKey: state.secrets.reCaptchaV2SiteKey,
});

const mapDispatchToProps = dispatch => ({
  getProfile: () => dispatch(getProfile()),
  secretsActions: new SecretsActions(dispatch, new SecretsService()),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(App));
